import React from "react";
import { t } from "ttag";

import { Clickable } from "../../Clickables";
import { Svg } from "../../Svg";
import { ContributionCard } from "../ContributionCard/ContributionCard";
import { Action, SuiteState, getJoiningKitTargets, hub } from "../engine";

import styles from "./SpokeContributionsList.module.scss";

/**
 * Per-spoke list of contribution cards, shared between the two surfaces
 * that render the suite summary. The two modes the spec calls for:
 *   - `landing` (Populated Landing): every spoke section renders, empty
 *     spokes show an "+ Add an Appliance" placeholder, each card has a
 *     Remove control.
 *   - `mini-cart` (Mini Cart overlay): empty spokes are omitted, no
 *     Remove control. Mid-walk is review-only per spec.
 */
export type SpokeContributionsListProps = {
    suite: SuiteState;
    dispatch: React.Dispatch<Action>;
    mode: "landing" | "miniCart";
};

export const SpokeContributionsList = ({
    suite,
    dispatch,
    mode,
}: SpokeContributionsListProps) => {
    const kitTargetIds = getJoiningKitTargets(suite);

    return hub.spokes.map((spoke) => {
        const inSpoke = suite.contributions.filter((c) => c.spoke === spoke.id);
        if (inSpoke.length === 0 && mode === "miniCart") return null;
        const enterSpoke = () =>
            dispatch({
                type: "ENTER_SPOKE",
                spoke: spoke.id,
                entry: "populated",
            });
        return (
            <div
                key={spoke.id}
                className={mode === "landing" ? styles.spoke : styles.spokeMini}
            >
                <div className={styles.spokeHeader}>
                    <h2 className={styles.spokeHeading}>{spoke.label}</h2>
                    {mode === "landing" && (
                        <Clickable
                            onClick={enterSpoke}
                            className={styles.textLinkAdd}
                        >
                            {t`Add an Appliance`}
                        </Clickable>
                    )}
                </div>
                {inSpoke.length === 0 ? (
                    <Clickable
                        onClick={enterSpoke}
                        className={styles.placeholder}
                    >
                        {t`Add an Appliance`}
                        <Svg
                            name="plus"
                            aria-hidden="true"
                            className={styles.placeholderSvg}
                        />
                    </Clickable>
                ) : (
                    inSpoke.map((c) => (
                        <ContributionCard
                            key={c.id}
                            contribution={c}
                            hasJoiningKit={kitTargetIds.has(c.id)}
                            mode={mode}
                            onRemove={
                                mode === "landing"
                                    ? () =>
                                          dispatch({
                                              type: "REMOVE_CONTRIBUTION",
                                              contributionId: c.id,
                                          })
                                    : undefined
                            }
                        />
                    ))
                )}
            </div>
        );
    });
};
